import { useCallback } from 'react'
import { createMeeting, createBlankActivity, updateActivity } from '../service/projects'
import { ProcessedMeeting, ProcessedActivity } from './useAILessonProcessor'
import { PLANNING_CONFIG } from '../utils/planningHelpers'

export interface MeetingCreationResult {
  meetingId: string
  date: string
  activitiesCreated: number
}

/**
 * Hook para criar meetings e activities no backend
 */
export function useMeetingCreator() {
  /**
   * Cria um meeting no backend
   */
  const createSingleMeeting = useCallback(async (
    projectId: string,
    meeting: ProcessedMeeting
  ): Promise<{ meetingId: string; date: string }> => {
    const meetingPayload = {
      project_id: projectId,
      date: meeting.date,
      duration: meeting.duration,
      time: meeting.time,
      title: meeting.title,
      enable_pendency_journal: PLANNING_CONFIG.ENABLE_PENDENCY_JOURNAL,
    }

    const response = await createMeeting(meetingPayload)

    if (response && response.alreadyMeetings && response.alreadyMeetings.length > 0) {
      throw new Error(
        `Já existe um encontro na data ${meeting.date}. Escolha uma data inicial diferente.`
      )
    }

    // Retorna o ID do meeting criado (precisa ser obtido via getActivities)
    return {
      meetingId: response.id || '',
      date: meeting.date,
    }
  }, [])

  /**
   * Cria uma atividade para um meeting
   */
  const createSingleActivity = useCallback(async (
    projectId: string,
    meetingId: string,
    activity: ProcessedActivity
  ): Promise<void> => {
    // Criar atividade em branco
    const blankActivity = await createBlankActivity({
      session_meeting_id: meetingId,
      project_id: projectId,
    })

    if (!blankActivity || !blankActivity.id) {
      throw new Error(`Falha ao criar atividade em branco`)
    }

    // Atualizar atividade com dados da IA
    const activityPayload = {
      id: blankActivity.id,
      project_id: projectId,
      title: activity.title,
      planning: activity.planning,
      has_assessments: false,
      activity_lv4_items_attributes: activity.activity_lv4_items_attributes,
    }

    await updateActivity(blankActivity.id, activityPayload)
  }, [])

  /**
   * Cria todas as atividades de um meeting
   */
  const createMeetingActivities = useCallback(async (
    projectId: string,
    meetingId: string,
    activities: ProcessedActivity[],
    onProgress?: (current: number, total: number) => void
  ): Promise<number> => {
    let activitiesCreated = 0

    for (let i = 0; i < activities.length; i++) {
      if (onProgress) {
        onProgress(i + 1, activities.length)
      }

      await createSingleActivity(projectId, meetingId, activities[i])
      activitiesCreated++
    }

    return activitiesCreated
  }, [createSingleActivity])

  return {
    createSingleMeeting,
    createSingleActivity,
    createMeetingActivities,
  }
}
